import ExportOutlined from "@ant-design/icons/ExportOutlined";
import { Download } from "lucide-react";
import LookIcon from "common/Icons/LookIcon";
import FilterOutlined from "@ant-design/icons/FilterOutlined";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Button from "common/Buttons/Button";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips";
import Flex from "common/Boxes/Flex";
import {
  CLOSED_TRADES_PATH,
  CREATE_TRADE_PATH,
  DEFAULT_PADDING,
  OPEN_TRADES_PATH,
  SMALL_MOBILE,
  SMALL_MOBILE_PADDING,
  TRADES_PATH,
} from "common/constants";
import StickyHeader from "common/Headers/StickyHeader";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import useMedia from "utils/hooks/useMedia";
import useTabsWithUrl from "common/Tabs/useTabsWithUrl";
import Tabs from "common/Tabs/Tabs";
import {
  closedTradesFilters,
  openTradesFilters,
} from "@shared/slices/filters.slice";
import { getTradesFilterTypeToTitle } from "@shared/features/trades/common/utils";
import BulkActions from "common/Table/BulkActions";
import { TradeActionsContext } from "@shared/features/trades/common/contexts";
import { useSelector } from "react-redux";
import FiltersCounter from "common/Filters/common/FiltersCounter";
import styled, { useTheme } from "styled-components";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context";
import { TabItem } from "common/Tabs/PageTabs";
import { useHistory } from "react-router-dom";
import { sharedLib } from "@shared/services/shared.service";
import { displayResultMessage } from "utils/misc";
import NoExchangeConnectedModal from "features/configuration/common/NoExchangeConnectedModal";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import {
  useIsSubUser,
  useSelectUser,
  useSubUserPermissions,
} from "@shared/utils/hooks/misc.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { serializeFilters } from "@shared/features/filters/utils.tsx";

interface TradesHeaderProps {
  filtersExpanded: boolean;
  setFiltersExpanded: (filtersExpanded: boolean) => void;
  useChipsFilters?: boolean;
  isOpen: boolean;
  showFilters?: boolean;
  exportButton?: React.JSX.Element;
  onCreateClick: () => void;
  noExchangeConnectedModal?: React.JSX.Element;
}

const TradesHeader = ({
  filtersExpanded,
  setFiltersExpanded,
  useChipsFilters,
  isOpen,
  showFilters,
  exportButton,
  onCreateClick,
  noExchangeConnectedModal,
}: TradesHeaderProps) => {
  const {
    selectedTradesIDs,
    setSelectedTradesIDs,
    actionButtonsData,
    isBulkMode,
    exitBulkMode,
    selectAll,
    isAllRowsSelected,
  } = useContext(TradeActionsContext)!;
  const { getTradesQuery } = useContext(TradesPageContext)!;
  const padding = useMedia(
    [`(max-width: ${SMALL_MOBILE})`],
    [SMALL_MOBILE_PADDING],
    DEFAULT_PADDING
  );
  const filters = isOpen ? openTradesFilters : closedTradesFilters;
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);

  const tabToDisplayName = {
    [TradesTab.OPEN]: t("tabs.open"),
    [TradesTab.CLOSED]: t("tabs.closed"),
  };

  const { tabDisplayName, onChange: onTabChange } = useTabsWithUrl<TradesTab>({
    tabToDisplayName,
    tabs: Object.values(TradesTab),
    path: TRADES_PATH,
    defaultTab: TradesTab.OPEN,
    keepQuery: false,
  });
  const isSubUser = useIsSubUser();
  const user = useSelectUser();

  const numActiveFilters = useSelector(
    filters.selectors.selectNumActiveFilters
  );

  const filtersChips = useMemo(() => {
    return (
      <FiltersChips
        filtersActions={filters.actions}
        filtersSelectors={filters.selectors}
        filterTypeToTitle={getTradesFilterTypeToTitle(isOpen)}
        style={FILTERS_STYLE}
      />
    );
  }, [filters, isOpen]);

  const getBulkActions = () => {
    if (!isBulkMode) return undefined;
    return (
      <BulkActions
        actionButtonsData={actionButtonsData}
        unselectAll={() => setSelectedTradesIDs([])}
        selectAll={selectAll}
        numSelected={selectedTradesIDs.length}
        onCancel={exitBulkMode}
        style={{ padding: `0 ${padding}px ${padding}px` }}
        isAllSelected={isAllRowsSelected}
      />
    );
  };

  const tabsItems: TabItem[] = [
    {
      key: tabToDisplayName[TradesTab.OPEN],
      label: tabToDisplayName[TradesTab.OPEN],
      link: OPEN_TRADES_PATH,
    },
    {
      key: tabToDisplayName[TradesTab.CLOSED],
      label: tabToDisplayName[TradesTab.CLOSED],
      link: CLOSED_TRADES_PATH,
    },
  ];

  return (
    <StickyHeader style={{ padding: `0 ${padding}px` }}>
      <HeaderInnerWrapper>
        <StyledTabs
          activeKey={tabDisplayName}
          onChange={onTabChange}
          items={tabsItems}
        />
        <Flex style={{ columnGap: 12 }}>
          {showFilters && (
            <ButtonWrapper>
              <Button
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                loading={getTradesQuery?.isFetching}
              >
                <FilterOutlined />
                {t("filters.open_filters")}
              </Button>
              <FiltersCounter numActiveFilters={numActiveFilters} />
            </ButtonWrapper>
          )}
          {exportButton}
          {!isSubUser && !user?.isWhopExperience() && (
            <Button type="primary" onClick={onCreateClick}>
              <PlusOutlined />
              {t("actions.create_trade")}
            </Button>
          )}
        </Flex>
      </HeaderInnerWrapper>
      <ExpandCollapse expanded={filtersExpanded && !!useChipsFilters}>
        {filtersChips}
      </ExpandCollapse>
      {getBulkActions()}
      {noExchangeConnectedModal}
    </StickyHeader>
  );
};

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;
  }
  .ant-tabs-tab {
    padding-top: 0;
  }
  margin-right: 20px;
`;
const HeaderInnerWrapper = styled(Flex)`
  align-items: flex-end;
  padding: 12px 0;
`;

export const useTradesHeader = (isOpen: boolean) => {
  const history = useHistory();
  const { isNewLook } = useTheme();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const { isReadOnly } = useSubUserPermissions();
  const defaultFiltersExpanded = useMedia(
    [`(max-width: 1500px)`],
    [false],
    true
  );
  const useChipsFilters = useMedia([`(max-width: 1300px)`], [true], false);
  const showFiltersButton = useMedia([`(max-width: 550px)`], [false], true);
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { filtersSlice } = useContext(TradesPageContext)!;

  const [filtersExpanded, setFiltersExpanded] = useState(
    defaultFiltersExpanded
  );
  const [noExchangeModalVisible, setNoExchangeModalVisible] = useState(false);
  const filtersSliceState = useSelector(filtersSlice.selectors.selectFilters);

  const [exportTrades, exportTradesStatus] =
    sharedLib.sharedApi.trades.useLazyExportTradesQuery();

  useEffect(() => {
    setFiltersExpanded(defaultFiltersExpanded);
  }, [defaultFiltersExpanded]);

  const onCreateClick = () =>
    user?.has_valid_clients_connected
      ? history.push(CREATE_TRADE_PATH)
      : setNoExchangeModalVisible(true);

  const onExportClick = useCallback(() => {
    displayResultMessage(
      exportTrades({
        open_trades: isOpen,
        closed_trades: !isOpen,
        filters: serializeFilters(filtersSliceState, isOpen),
      }).unwrap()
    )
      .then((downloadLink) => {
        if (downloadLink) {
          window.location.href = downloadLink;
        }
      })
      .catch(() => {});
  }, [isOpen, filtersSliceState]);

  const exportButton = useMemo(() => {
    return (
      <Button
        // New look: a quiet outlined pill next to the Filters pill.
        $type={isNewLook ? undefined : "secondary"}
        onClick={onExportClick}
        loading={exportTradesStatus.isLoading}
      >
        <LookIcon
          classic={<ExportOutlined />}
          newLook={<Download size={16} strokeWidth={1.75} />}
        />
        {t("actions.export")}
      </Button>
    );
  }, [exportTradesStatus.isLoading, isOpen, onExportClick, isNewLook]);

  const noExchangeConnectedModal = (
    <NoExchangeConnectedModal
      placement="trades_header"
      open={noExchangeModalVisible}
      onClose={() => setNoExchangeModalVisible(false)}
      action={t("modals.no_exchange_action_create")}
    />
  );

  const tradesHeader = (
    <TradesHeader
      filtersExpanded={filtersExpanded || !showFiltersButton}
      setFiltersExpanded={setFiltersExpanded}
      useChipsFilters={useChipsFilters}
      isOpen={isOpen}
      showFilters={showFiltersButton}
      exportButton={
        IS_TELEGRAM_WEB_APP || isReadOnly ? undefined : exportButton
      }
      onCreateClick={onCreateClick}
      noExchangeConnectedModal={noExchangeConnectedModal}
    />
  );

  return {
    filtersExpanded: !useChipsFilters && filtersExpanded,
    setFiltersExpanded,
    tradesHeader,
    exportButton,
    onExportClick,
    isExporting: exportTradesStatus.isLoading,
    noExchangeConnectedModal,
    onCreateClick,
    // Used by the new-look layout, which renders the header pieces itself.
    isFiltersToggled: filtersExpanded,
    chipsFiltersExpanded:
      useChipsFilters && (filtersExpanded || !showFiltersButton),
    showFiltersButton,
    showExportButton: !IS_TELEGRAM_WEB_APP && !isReadOnly,
  };
};

enum TradesTab {
  OPEN = "open",
  CLOSED = "closed",
}

const FILTERS_STYLE = { padding: "0 0 12px" };

const ButtonWrapper = styled.div`
  position: relative;
`;
